import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Key, BarChart3, Home, Book, Database, Shield, Menu, X, DollarSign, HelpCircle, UserCircle } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';

  const navigation = [
    { name: 'Dashboard',      href: '/dashboard',      icon: Home,       tourId: 'nav-dashboard'      },
    { name: 'API Keys',       href: '/api-keys',       icon: Key,        tourId: 'nav-api-keys'       },
    { name: 'Usage',          href: '/usage',          icon: BarChart3,  tourId: 'nav-usage'          },
    ...(subscriptionsEnabled ? [{ name: 'Plans', href: '/plans', icon: DollarSign, tourId: 'nav-plans' }] : []),
    { name: 'Models',         href: '/models',         icon: Database,   tourId: 'nav-models'         },
    { name: 'API Reference',  href: '/api-reference',  icon: Book,       tourId: 'nav-api-reference'  },
    { name: 'Category Rules', href: '/category-rules', icon: Shield,     tourId: 'nav-category-rules' },
  ];

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left — brand */}
          <div className="flex-shrink-0 flex items-center w-40">
            <h1 className="text-xl font-bold text-gray-900">AskArc API</h1>
          </div>

          {/* Centre — nav items */}
          <div className="hidden lg:flex lg:items-center lg:space-x-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-tour={item.tourId}
                  className={`${
                    isActive
                      ? 'border-indigo-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1`}
                >
                  <Icon size={16} />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-2 w-40 justify-end">
            {/* Guide help icon */}
            <Link
              to="/guide"
              data-tour="nav-guide"
              title="How it all works"
              className={`p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                location.pathname === '/guide' ? 'text-indigo-500' : 'text-gray-400 hover:text-gray-500'
              }`}
            >
              <HelpCircle size={20} />
            </Link>

            {/* User menu */}
            <div className="relative hidden lg:block" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className={`p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  userMenuOpen ? 'text-indigo-500' : 'text-gray-400 hover:text-gray-500'
                }`}
              >
                <UserCircle size={22} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-400 mb-0.5">Signed in as</p>
                    <p className="text-sm font-medium text-gray-800 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} />
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${
                    isActive
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center gap-2`}
                >
                  <Icon size={18} />
                  {item.name}
                </Link>
              );
            })}
          </div>
          <div className="border-t border-gray-200 px-4 py-3 space-y-2">
            <p className="text-sm text-gray-500 truncate">{user?.email}</p>
            <button
              onClick={() => { setMobileMenuOpen(false); logout(); }}
              className="flex items-center gap-2 text-sm text-red-600"
            >
              <LogOut size={15} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
